// SC-N2 — scoped styles for the notes editor sheet. Injected once, idempotent.
// Uses the existing index.html design tokens (--teal, --navy, --slate, --mint-l, etc.).
// Responsive by viewport breakpoint (>=1024 / 768-1023 / <768) per the SC-N2 decision.
// Logical properties throughout so RTL mirrors without a blanket direction flip.
let _injected = false;
export function injectNotesStyles(){
  if(_injected || document.getElementById('vn2-styles')) { _injected = true; return; }
  const css = `
/* ── Sheet container (built in index.js _mount) ── */
.vn2-overlay{position:fixed;inset:0;z-index:450;display:flex;background:rgba(15,26,24,.38);}
.vn2-overlay.vn2-phone{background:transparent;}
.vn2-sheet{background:#fff;display:flex;flex-direction:column;overflow:hidden;
  box-shadow:0 8px 24px rgba(13,148,136,.12),0 4px 8px rgba(0,0,0,.10);}
/* desktop >=1024: ~24px inset */
.vn2-overlay:not(.vn2-tablet):not(.vn2-phone){align-items:stretch;justify-content:stretch;padding:24px;}
.vn2-overlay:not(.vn2-tablet):not(.vn2-phone) .vn2-sheet{flex:1;border-radius:16px;}
/* tablet 768-1023: ~16px inset */
.vn2-overlay.vn2-tablet{align-items:stretch;justify-content:stretch;padding:16px;}
.vn2-overlay.vn2-tablet .vn2-sheet{flex:1;border-radius:14px;}
/* phone <768: full-bleed */
.vn2-overlay.vn2-phone{align-items:stretch;justify-content:stretch;padding:0;}
.vn2-overlay.vn2-phone .vn2-sheet{flex:1;border-radius:0;
  padding-top:env(safe-area-inset-top);padding-bottom:env(safe-area-inset-bottom);}

/* ── Root column (returned by editor.js) ── */
.vn2-root{height:100%;display:flex;flex-direction:column;min-height:0;background:#fff;overflow:hidden;}

/* ── Header ── */
.vn2-header{flex:0 0 auto;display:flex;align-items:center;gap:10px;
  padding:10px 14px;border-bottom:1px solid var(--mint-l);min-height:52px;}
.vn2-back{display:inline-flex;align-items:center;gap:6px;background:none;border:none;
  color:var(--slate);font-weight:700;font-size:.82rem;cursor:pointer;padding:6px 8px;border-radius:8px;
  transition:background .15s,color .15s;}
.vn2-back:hover{background:var(--mint-ll);color:var(--navy);}
/* Back chevron mirrors with document direction (logical, not language-specific) */
.vn2-chev{display:inline-block;}
.vn2-avatar{width:26px;height:26px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;
  justify-content:center;background:var(--mint-ll);font-size:.9rem;overflow:hidden;}
.vn2-avatar img{width:100%;height:100%;object-fit:cover;}
.vn2-patient{font-weight:800;font-size:.82rem;color:var(--navy);white-space:nowrap;
  max-width:130px;overflow:hidden;text-overflow:ellipsis;flex-shrink:0;}
.vn2-title-wrap{display:flex;flex:1;min-width:0;align-items:center;gap:8px;}
.vn2-title{flex:1;min-width:40px;border:none;border-bottom:1.5px solid transparent;background:transparent;
  font-weight:700;font-size:.9rem;color:var(--navy);padding:4px 2px;outline:none;
  transition:border-color .15s;font-family:inherit;}
/* F-27: the title field + its placeholder element share one grid cell; the placeholder sizes the cell. Its flex base
   size is the one-line text width, so in the wrapping header row (F-25) the title drops to its own line instead of
   squeezing; only a title wider than a whole line wraps. */
.vn2-title-box{flex:1 1 auto;min-width:0;max-width:100%;display:grid;align-items:center;}
.vn2-title-box > .vn2-title{grid-area:1/1;min-width:0;width:100%;height:100%;}
.vn2-title-ph{grid-area:1/1;pointer-events:none;font-weight:700;font-size:.9rem;color:var(--slate-l);padding:4px 2px;
  border-bottom:1.5px solid transparent;white-space:normal;overflow-wrap:anywhere;text-align:start;}
.vn2-title:not(:placeholder-shown) + .vn2-title-ph{visibility:hidden;}
.vn2-title:disabled + .vn2-title-ph{opacity:.75;}
.vn2-title:focus{border-bottom-color:var(--teal);}
.vn2-title:disabled{color:var(--slate);opacity:.75;}
.vn2-chip{flex-shrink:0;font-size:.64rem;font-weight:800;padding:3px 10px;border-radius:999px;
  text-transform:uppercase;letter-spacing:.04em;white-space:nowrap;}
/* Notes SC2 — autosave status pill (mock v3): dot + text, updated in place by the engine */
.vn2-pill{flex-shrink:0;display:inline-flex;align-items:center;gap:6px;font-size:.74rem;font-weight:600;
  padding:4px 11px;border-radius:999px;white-space:nowrap;background:#f1f5f9;color:var(--slate);}
.vn2-pill[hidden]{display:none;}
.vn2-pill-dot{width:7px;height:7px;border-radius:50%;background:currentColor;flex-shrink:0;}
.vn2-pill-saved{background:#ecfdf5;color:#047857;}
.vn2-pill-editing{background:#f1f5f9;color:var(--slate);}
.vn2-pill-saving{background:var(--mint-ll);color:var(--teal-d);}
.vn2-pill-saving .vn2-pill-dot{animation:vn2PillPulse .8s infinite alternate;}
.vn2-pill-retrying,.vn2-pill-offline,.vn2-pill-offline_kept{background:#fffbeb;color:#b45309;}
.vn2-pill-conflict{background:#fffbeb;color:#92400e;}.vn2-pill-conflict .vn2-pill-dot{background:#f59e0b;} /* SC3: paused */
/* Notes SC3 (C2): conflict / deleted-elsewhere banner + Restore chip (mock notes-conflict) — logical props (RTL-safe) */
.vn2-conflict-slot:empty{display:none;}
.vn2-conflict-slot{padding:10px 16px 0;}
.vn2-conflict{border-radius:12px;padding:12px 14px;display:flex;gap:12px;align-items:flex-start;flex-wrap:wrap;}
.vn2-conflict-conflict{background:#fffbeb;border:1px solid #fde68a;}
.vn2-conflict-deleted{background:#fef2f2;border:1px solid #fecaca;}
.vn2-conflict-ic{font-size:20px;line-height:1.2;}
.vn2-conflict-tx{flex:1;min-width:220px;}
.vn2-conflict-t1{font-weight:700;font-size:14px;color:var(--navy);}
.vn2-conflict-t2{font-size:13px;color:var(--slate);margin-top:2px;}
.vn2-conflict-acts{display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;}
.vn2-cbtn{border:none;border-radius:10px;padding:8px 14px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit;}
.vn2-cbtn:disabled{opacity:.6;cursor:default;}
.vn2-cbtn-p{background:var(--teal);color:#fff;}
.vn2-cbtn-s{background:#fff;color:var(--navy);border:1.5px solid #c4dbd8;}
.vn2-cbtn-l{background:none;color:var(--teal-d);text-decoration:underline;padding-inline:4px;}
.vn2-restore{display:inline-flex;gap:8px;align-items:center;background:#f5fafa;border:1px solid var(--mint-l);border-radius:999px;padding:5px 12px;font-size:12.5px;}
.vn2-restore button{border:none;background:none;color:var(--teal-d);font-weight:700;cursor:pointer;font-family:inherit;font-size:12.5px;}
@keyframes vn2PillPulse{from{opacity:.3}to{opacity:1}}
.vn2-hactions{display:flex;align-items:center;gap:6px;margin-inline-start:auto;flex-shrink:0;position:relative;}
.vn2-overflow{background:none;border:1px solid var(--mint-l);border-radius:8px;color:var(--slate);
  cursor:pointer;font-size:1rem;line-height:1;padding:5px 9px;transition:background .15s;}
.vn2-overflow:hover{background:var(--mint-ll);}
.vn2-menu{position:absolute;inset-inline-end:0;top:calc(100% + 6px);background:#fff;
  border:1px solid var(--mint-l);border-radius:10px;box-shadow:0 4px 12px rgba(13,148,136,.10),0 2px 4px rgba(0,0,0,.06);
  padding:6px;display:flex;flex-direction:column;gap:2px;min-width:150px;z-index:5;}
.vn2-menu button{justify-content:flex-start;text-align:start;width:100%;}

/* ── Body / panes ── */
.vn2-body{flex:1;min-height:0;display:flex;flex-direction:column;}
.vn2-pane{display:flex;flex-direction:column;min-height:0;min-width:0;flex:1;padding:12px 14px;}
.vn2-pane-clinical{}
.vn2-pane-parent{}
.vn2-pane-hd{flex:0 0 auto;display:flex;align-items:center;gap:8px;margin-bottom:8px;flex-wrap:wrap;}
/* Pane headers read as section titles of one document (not uppercase input labels) */
.vn2-pane-hd-label{display:inline-flex;align-items:center;gap:6px;font-size:.82rem;font-weight:800;
  color:var(--navy);text-transform:none;letter-spacing:normal;}
.vn2-badge{font-size:.6rem;font-weight:800;padding:2px 7px;border-radius:999px;text-transform:uppercase;letter-spacing:.03em;}
.vn2-badge-private{background:var(--mint-ll);color:var(--slate);}
.vn2-badge-parents{background:#dbeafe;color:#1e40af;}
.vn2-pane-actions{display:flex;align-items:center;gap:4px;margin-inline-start:auto;flex-wrap:wrap;}
.vn2-scroll{flex:1;min-height:0;display:flex;flex-direction:column;}
.vn2-ta-wrap{flex:1;min-height:0;width:100%;display:flex;}
/* Borderless textareas on a faint tinted surface — the two panes read as one document.
   Subtle focus ring only; comfortable reading type. */
.vn2-ta{flex:1;width:100%;min-height:0;resize:none;overflow:auto;box-sizing:border-box;
  border:none;border-radius:var(--r-md);padding:16px 18px;
  font-family:inherit;font-size:16px;line-height:1.7;color:var(--navy);background:#f7fbfb; /* SC6a (decision 25.15): 16px at EVERY width — iOS/iPadOS zoom on focus below 16px */
  transition:box-shadow .15s,background .15s;}
.vn2-ta::placeholder{color:var(--slate-l);}
.vn2-ta:focus{outline:none;background:#fff;box-shadow:0 0 0 3px rgba(13,148,136,.16);}
.vn2-ta[readonly],.vn2-ta:disabled{background:#f4f7f7;color:var(--slate);}
/* Clinical fills the pane with generous inline padding (no centered measure cap) */
.vn2-pane-clinical .vn2-ta{padding:18px 28px;}
/* ══ SC6a — the rich clinical field (Tiptap). Same surface as .vn2-ta; 16px at every width; logical props only;
   every block has dir="auto" (textDirection), so Hebrew and English lines align independently. ══ */
.vn2-rich-wrap{flex-direction:column;}
.vn2-rich-host{flex:1;min-height:0;display:flex;flex-direction:column;position:relative;overflow:auto;box-sizing:border-box;
  border-radius:var(--r-md);background:#f7fbfb;transition:box-shadow .15s,background .15s;}
.vn2-rich-host:focus-within{background:#fff;box-shadow:0 0 0 3px rgba(13,148,136,.16);}
.vn2-rich-host.vn2-rich-disabled,.vn2-rich-host.vn2-rich-ro{background:#f4f7f7;}
.vn2-rich-host[data-loading]::before{content:attr(data-loading);position:absolute;inset-inline-start:28px;top:18px;color:var(--slate-l);font-size:16px;pointer-events:none;}
.vn2-rich-host[data-pending]::before{content:attr(data-pending);position:absolute;inset-inline-start:28px;top:18px;color:var(--slate-l);font-size:16px;pointer-events:none;}
.vn2-rich-host[data-pending] .vn2-rich{visibility:hidden;}
.vn2-rich{flex:1;min-height:100%;outline:none;box-sizing:border-box;padding:18px 28px;font-family:inherit;font-size:16px;line-height:1.7;color:var(--navy);word-wrap:break-word;white-space:pre-wrap;}
.vn2-rich-ro .vn2-rich,.vn2-rich-disabled .vn2-rich{color:var(--slate);}
.vn2-rich > *:first-child{margin-top:0;}
.vn2-rich p{margin:0;min-height:1.7em;}
.vn2-rich h3{font-size:17px;font-weight:800;line-height:1.4;margin:14px 0 4px;color:var(--navy);}
.vn2-rich ul,.vn2-rich ol{margin:2px 0;padding-inline:24px;} /* both sides: an RTL item in an LTR list (or the reverse) keeps room for its marker */
.vn2-rich li > p{min-height:0;}
.vn2-rich li{text-align:start;} /* F-55: WebKit's UA sheet gives li text-align:match-parent, which resolves against the PARENT list's direction, so a Hebrew item in an LTR list kept its text on the left while its marker moved right */
.vn2-rich mark{background:#fef08a;color:inherit;border-radius:2px;padding:0 1px;}
.vn2-rich u{text-decoration-thickness:1px;text-underline-offset:2px;}
.vn2-rich p.is-editor-empty:first-child::before{content:attr(data-placeholder);color:var(--slate-l);float:inline-start;height:0;pointer-events:none;}
.vn2-rich .vn2-interim{color:var(--slate-l);font-style:italic;} /* dictation: interim speech (a decoration — never in the doc) */
/* SC6c — dictation runs: "Cleaning up…" shimmer on the run in flight; a short mint flash on the edited range; the chip by
   the mic; the notice bar (AI-edited + Undo / kept / offline). */
.vn2-rich .vn2-dict-working{background:linear-gradient(90deg,transparent 0%,var(--mint-ll) 50%,transparent 100%);background-size:200% 100%;animation:vn2-shimmer 1.2s infinite;}
@keyframes vn2-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
.vn2-rich .vn2-dict-edited{animation:vn2-edited 1.6s ease-out;border-radius:2px;}
@keyframes vn2-edited{from{background:#bbf7d0}to{background:transparent}}
.vn2-dict-status{display:inline-flex;align-items:center;gap:6px;font-size:.7rem;font-weight:700;color:var(--teal-d);background:var(--mint-ll);border-radius:999px;padding:3px 10px;}
.vn2-dict-status[hidden]{display:none;}
.vn2-dict-notice{flex:0 0 auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:6px 14px 0;padding:7px 12px;border-radius:8px;font-size:.8rem;font-weight:600;line-height:1.4;background:#ecfdf5;color:var(--teal-d);border:1px solid var(--mint-l);}
.vn2-dict-notice[hidden]{display:none;}
.vn2-dict-notice-kept,.vn2-dict-notice-offline,.vn2-dict-notice-tidy_kept,.vn2-dict-notice-tidy_too_long,.vn2-dict-notice-production_kept,.vn2-dict-notice-tidy_production_kept{background:#fffbeb;color:#92400e;border-color:#fde68a;}
.vn2-rich .vn2-dict-numfix{background:#fef08a;border-radius:2px;box-shadow:0 0 0 1px #facc15;}
.vn2-dict-notice-numfix{flex-basis:100%;order:2;font-weight:600;color:#92400e;}
.vn2-dict-notice-hint{background:var(--mint-ll,#f0fdf9);color:var(--navy,#1a2e2b);font-weight:500;}
.vn2-dict-notice-undo{margin-inline-start:auto;border:none;background:none;color:var(--teal-d);font-weight:800;cursor:pointer;font-family:inherit;font-size:.8rem;min-height:32px;padding:0 6px;}
@media (max-width:767px){ .vn2-dict-notice-undo{min-height:44px;} }
/* SC6a R4 — the formatting toolbar: one row above the editor (it scrolls horizontally on narrow screens); 36px targets on
   desktop, 44px on phones; logical props only (RTL reads right to left). */
.vn2-tb{flex:0 0 auto;display:flex;align-items:center;gap:2px;padding:4px 8px;margin-bottom:6px;overflow-x:auto;scrollbar-width:none;
  background:#fff;border:1px solid var(--mint-l);border-radius:10px;z-index:3;}
.vn2-tb::-webkit-scrollbar{display:none;}
.vn2-tb-btn{flex:0 0 auto;min-width:36px;height:36px;padding:0 8px;border:none;border-radius:8px;background:transparent;color:var(--navy);
  font-family:inherit;font-size:15px;font-weight:700;line-height:1;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;}
.vn2-tb-btn:hover:not(:disabled){background:var(--mint-ll);}
.vn2-tb-btn:focus-visible{outline:2px solid var(--teal);outline-offset:1px;}
.vn2-tb-btn.vn2-tb-on{background:var(--mint-l);color:var(--teal-d);}
.vn2-tb-btn:disabled{opacity:.35;cursor:default;}
.vn2-tb-i span{font-style:italic;font-family:Georgia,serif;}
.vn2-tb-dir span{font-size:11px;font-weight:800;letter-spacing:.02em;direction:ltr;unicode-bidi:isolate;}
.vn2-tb-u span{text-decoration:underline;text-underline-offset:2px;}
.vn2-tb-sep{flex:0 0 auto;width:1px;height:20px;background:var(--mint-l);margin:0 4px;}
.vn2-tb-pinned{position:fixed;inset-inline:8px;margin:0;box-shadow:0 4px 12px rgba(13,148,136,.16);z-index:460;}
@media (max-width:767px){ .vn2-tb{padding:2px 4px;} .vn2-tb-btn{min-width:44px;height:44px;font-size:16px;} }
@media (max-width:767px){ .vn2-rich{padding:14px 16px;} .vn2-rich-host[data-loading]::before,.vn2-rich-host[data-pending]::before{inset-inline-start:16px;top:14px;} }
/* Notes SC1: inline error bar (under the header) + Retry — logical props only (RTL-safe) */
.vn2-body[hidden]{display:none!important}
.vn2-offline-card{flex:1 1 auto;display:flex;align-items:center;justify-content:center;padding:32px 20px;text-align:center}
.vn2-offline-tx{max-width:360px;font-size:15px;line-height:1.6;color:var(--slate,#4a7570);background:#f0fdf9;border:1px solid #ccfbf1;border-radius:14px;padding:18px 20px}
.vn2-errbar{flex:0 0 auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin:8px 14px 0;
  background:#fef2f2;border:1px solid #fecaca;color:#991b1b;border-radius:8px;padding:8px 12px;
  font-size:.8rem;font-weight:600;line-height:1.4;}
.vn2-errbar[hidden]{display:none;}
.vn2-errbar-msg{flex:1 1 auto;min-width:0;}
.vn2-errbar-retry{flex:0 0 auto;min-height:32px;border:1.5px solid #dc2626;background:#fff;color:#b91c1c;
  border-radius:8px;padding:4px 12px;font:inherit;font-weight:700;cursor:pointer;}
.vn2-errbar-retry:hover{background:#fee2e2;}
.vn2-locked-banner{flex:0 0 auto;margin-bottom:8px;background:#fef3c7;color:#92400e;border-radius:8px;
  padding:6px 10px;font-size:.72rem;font-weight:700;}
.vn2-pane-footer{flex:0 0 auto;display:flex;align-items:center;gap:10px;margin-top:10px;}
.vn2-footer-status{font-size:.7rem;color:var(--slate-l);}
.vn2-pane-footer .btn-md{margin-inline-start:auto;}

/* free-tier upgrade card centered */
.vn2-upgrade{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:8px;background:#f8fafc;border:1.5px dashed var(--mint-l);border-radius:var(--r-md);padding:24px 16px;
  text-align:center;cursor:pointer;}

/* collapse rail (>=1024) */
.vn2-rail{display:none;flex:1;min-height:0;flex-direction:column;align-items:center;justify-content:flex-start;
  gap:10px;padding-top:8px;cursor:pointer;}
.vn2-rail-label{writing-mode:vertical-rl;transform:rotate(180deg);font-size:.68rem;font-weight:800;
  color:var(--slate);text-transform:uppercase;letter-spacing:.08em;}

/* segmented switcher (phone) */
.vn2-switcher{flex:0 0 auto;display:flex;gap:4px;margin:10px 14px 6px;background:var(--mint-ll);
  border-radius:10px;padding:3px;}
.vn2-seg{flex:1;display:inline-flex;align-items:center;justify-content:center;gap:6px;border:none;
  background:transparent;color:var(--slate);font-weight:700;font-size:.76rem;padding:7px 8px;border-radius:8px;
  cursor:pointer;transition:background .15s,color .15s;}
.vn2-seg.vn2-seg-active{background:#fff;color:var(--navy);box-shadow:0 1px 3px rgba(0,0,0,.06);}
.vn2-dot{width:7px;height:7px;border-radius:50%;border:1.5px solid var(--teal);box-sizing:border-box;}
.vn2-dot.vn2-dot-filled{background:var(--teal);}

.vn2-pane-hidden{display:none !important;}

/* ── Phone header (<768): title gets the space; chip stacks under it as a compact dot+label ── */
@media (max-width:767px){
  .vn2-back-txt{display:none;}
  .vn2-avatar{display:none;}
  .vn2-patient{display:none;}
  .vn2-header{gap:8px;padding:8px 10px;}
  .vn2-title-wrap{flex-direction:column;align-items:flex-start;gap:1px;}
  .vn2-title{width:100%;}
  .vn2-title-box{width:100%;flex:none;} /* F-27 */
  .vn2-chip{background:transparent !important;padding:0;font-size:.62rem;letter-spacing:.02em;
    display:inline-flex;align-items:center;gap:5px;align-self:flex-start;max-width:100%;}
  .vn2-chip::before{content:"";width:7px;height:7px;border-radius:50%;background:currentColor;flex-shrink:0;}
  .vn2-pill{background:transparent !important;padding:0;font-size:.68rem;align-self:flex-start;max-width:100%;} /* SC2: compact, like the chip */
  .vn2-ta{font-size:16px;} /* SC2 A3: iOS zooms on focus below 16px (clinical + summary) */
  .vn2-title, .vn2-title-ph{font-size:16px;} /* SC2 A4: the title input too (F-27: + its placeholder) */
  .vn2-sess{padding:1px 0;} /* SC5 L3: the chip stacks above the title, full width */
}

/* ── >=768: two panes side by side, no switcher ── */
@media (min-width:768px){
  .vn2-body{flex-direction:row;}
  .vn2-switcher{display:none;}
  .vn2-pane{display:flex !important;}
  .vn2-pane-parent{border-inline-start:1px solid var(--mint-l);}
  .vn2-pane-clinical{flex:1;}
  .vn2-pane-parent{flex:1;}
}
/* ── >=1024: clinical:parent = 3:2, collapse enabled ── */
@media (min-width:1024px){
  .vn2-pane-clinical{flex:3;}
  .vn2-pane-parent{flex:2;}
  .vn2-pane-parent.vn2-collapsed{flex:0 0 48px;padding:8px 6px;cursor:pointer;}
  .vn2-pane-parent.vn2-collapsed .vn2-collapse-inner{display:none;}
  .vn2-pane-parent.vn2-collapsed .vn2-rail{display:flex;}
}

/* ══ N2b — Past-sessions rail / popover / peek / phone pane ══ */
/* The sheet interior anchors the floating past-session window (clamped inside it). */
.vn2-root{position:relative;}

/* Rail column (>=1024 only): rail | clinical 3fr | parent 2fr */
.vn2-rail-past{display:none;flex:0 0 160px;min-width:0;min-height:0;flex-direction:column;
  border-inline-end:1px solid var(--mint-l);padding-inline-end:8px;margin-inline-end:4px;overflow:hidden;}
@media (min-width:1024px){ .vn2-rail-past{display:flex;} }
.vn2-rail-past-hd{flex:0 0 auto;font-size:.7rem;font-weight:800;text-transform:uppercase;letter-spacing:.05em;
  color:var(--slate);padding:2px 4px 8px;}
.vn2-rail-list{flex:1;min-height:0;overflow:auto;display:flex;flex-direction:column;gap:6px;}
.vn2-rail-row{text-align:start;background:none;border:1px solid var(--mint-l);border-radius:9px;padding:7px 8px;
  cursor:pointer;display:flex;flex-direction:column;gap:4px;transition:background .15s,border-color .15s;font-family:inherit;}
.vn2-rail-row:hover{background:var(--mint-ll);border-color:var(--teal);}
.vn2-rail-row-top{display:flex;align-items:center;gap:6px;}
.vn2-rail-date{font-size:.68rem;color:var(--slate);font-weight:700;}
.vn2-rail-chip{font-size:.54rem;font-weight:800;padding:2px 6px;border-radius:999px;text-transform:uppercase;
  letter-spacing:.03em;white-space:nowrap;margin-inline-start:auto;}
.vn2-rail-title{font-size:.72rem;color:var(--navy);font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.vn2-rail-empty{font-size:.7rem;color:var(--slate-l);padding:8px 4px;}

/* Popover trigger (768-1023 only) + popover */
.vn2-past-trigger{display:none;background:none;border:1px solid var(--mint-l);border-radius:8px;color:var(--slate);
  cursor:pointer;font-size:.68rem;font-weight:700;padding:3px 8px;align-items:center;}
@media (min-width:768px) and (max-width:1023px){ .vn2-past-trigger{display:inline-flex;} }
.vn2-popover{position:absolute;top:calc(100% + 6px);inset-inline-end:0;z-index:8;background:#fff;
  border:1px solid var(--mint-l);border-radius:12px;box-shadow:0 6px 18px rgba(13,148,136,.12),0 2px 6px rgba(0,0,0,.08);
  padding:8px;min-width:220px;max-width:280px;max-height:60vh;overflow:auto;display:flex;flex-direction:column;gap:6px;}

/* Floating past-session window (>=768) — one at a time, draggable, clamped inside the sheet.
   Floats above the panes (z 30, still inside the sheet so modals/confirms cover it). */
/* Default: bottom inline-end corner, raised above the parent-pane footer so Publish is clear on open.
   Default size 380 x min(45% sheet, 420); min 280x200; resize/drag clamp to the sheet inner - 32. */
.vn2-peek-win{display:none;position:absolute;z-index:30;inset-inline-end:16px;bottom:72px;
  width:min(380px,calc(100% - 32px));height:min(45%,420px);min-width:280px;min-height:200px;
  flex-direction:column;overflow:hidden;
  background:#fff;border:1.5px solid var(--mint-l);border-radius:12px;
  box-shadow:0 12px 32px rgba(13,148,136,.20),0 4px 10px rgba(0,0,0,.14);}
@media (min-width:768px){ .vn2-peek-win.vn2-peek-open{display:flex;} }
.vn2-peek-win:focus{outline:none;} /* focused programmatically for Esc targeting; the border/shadow read as focus */
/* Window header = drag handle (touch-friendly) */
.vn2-pw-hd{flex:0 0 auto;display:flex;align-items:center;gap:8px;padding:8px 10px 8px 12px;
  border-bottom:1px solid var(--mint-l);background:var(--mint-ll);cursor:grab;touch-action:none;user-select:none;}
.vn2-pw-hd:active{cursor:grabbing;}
.vn2-peek-grip{color:var(--slate-l);font-size:.8rem;line-height:1;flex-shrink:0;}
.vn2-peek-date{font-size:.72rem;color:var(--slate);font-weight:700;}
.vn2-peek-title{font-weight:800;font-size:.84rem;color:var(--navy);flex:1;min-width:0;overflow:hidden;
  text-overflow:ellipsis;white-space:nowrap;}
.vn2-peek-ro{font-size:.58rem;font-weight:800;padding:2px 7px;border-radius:999px;background:#fff;
  color:var(--slate);text-transform:uppercase;letter-spacing:.03em;white-space:nowrap;}
.vn2-peek-actions{display:flex;gap:4px;margin-inline-start:auto;}
.vn2-peek-close{background:none;border:1px solid var(--mint-l);border-radius:7px;color:var(--slate);cursor:pointer;
  font-size:.66rem;font-weight:700;padding:3px 8px;}
.vn2-peek-close:hover{background:#fff;}
.vn2-peek-body{font-size:.8rem;line-height:1.6;color:var(--navy);white-space:pre-wrap;word-break:break-word;
  overflow:auto;padding:12px 14px;}
/* Window body has no padding — each section (clinical / toggle / summary) self-pads so the
   collapsible summary flows in the same scroll area. */
.vn2-peek-win .vn2-peek-body{flex:1;min-height:0;padding:0;}
.vn2-peek-clin{padding:12px 14px;}
.vn2-peek-sumtog{display:flex;align-items:center;gap:6px;width:100%;background:none;border:none;
  padding:8px 14px;cursor:pointer;font-family:inherit;font-size:.72rem;font-weight:800;color:var(--slate);text-align:start;}
.vn2-peek-sumtog:hover{background:var(--mint-ll);}
.vn2-peek-chev{margin-inline-start:auto;display:inline-block;font-size:.7rem;transition:transform .15s;}
.vn2-peek-chev.vn2-peek-chev-open{transform:rotate(90deg);}
[dir="rtl"] .vn2-peek-chev{transform:scaleX(-1);}
[dir="rtl"] .vn2-peek-chev.vn2-peek-chev-open{transform:rotate(90deg);}
.vn2-peek-divider{height:1px;background:var(--mint-l);margin:0 14px;}
.vn2-peek-sum-label{padding:8px 14px 2px;font-size:.62rem;font-weight:800;text-transform:uppercase;
  letter-spacing:.04em;color:var(--slate-l);}
.vn2-peek-sum{padding:2px 14px 12px;font-size:.78rem;line-height:1.55;color:var(--slate);white-space:pre-wrap;word-break:break-word;}
/* Resize grip — bottom inline-end corner (diagonal ticks) */
.vn2-peek-resize{position:absolute;inset-inline-end:2px;bottom:2px;width:16px;height:16px;z-index:2;
  cursor:nwse-resize;touch-action:none;
  background:linear-gradient(135deg,transparent 46%,var(--slate-l) 46%,var(--slate-l) 54%,transparent 54%,
    transparent 66%,var(--slate-l) 66%,var(--slate-l) 74%,transparent 74%);}
[dir="rtl"] .vn2-peek-resize{cursor:nesw-resize;transform:scaleX(-1);}

/* Phone detail (in the 3rd segment pane) reuses the content classes above with a plain header */
.vn2-peek-hd{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}

/* Phone: past sessions as the 3rd segment pane (hidden 768+, rail/popover take over) */
@media (min-width:768px){ .vn2-pane-past{display:none !important;} }

/* ══ N3 — closed LIST rows (rendered in #root, all tiers) ══ */
.vn2-list{display:flex;flex-direction:column;gap:10px;}
.vn2-lr{display:flex;flex-direction:column;gap:5px;width:100%;text-align:start;cursor:pointer;font-family:inherit;
  background:#fff;border:1.5px solid var(--mint-l);border-radius:12px;padding:12px 14px;
  transition:border-color .15s,background .15s;}
.vn2-lr:hover{border-color:var(--teal);background:var(--mint-ll);}
.vn2-lr-open{border-color:var(--teal);border-width:2px;padding:11.5px 13.5px;}
.vn2-lr-head{display:flex;align-items:center;gap:8px;min-width:0;}
.vn2-lr-chip{flex-shrink:0;font-size:.58rem;font-weight:800;padding:2px 8px;border-radius:999px;
  text-transform:uppercase;letter-spacing:.03em;white-space:nowrap;}
.vn2-lr-main{flex:1;min-width:0;display:flex;align-items:baseline;gap:8px;flex-wrap:wrap;}
.vn2-lr-custom{flex-basis:100%;font-size:.76rem;color:var(--slate);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;} /* SC5: custom title, 2nd line */
/* ══ SC5 L3 — the session chip + picker menu + move dialog (mock v3) ══ */
.vn2-sess{flex-shrink:1;min-width:0;max-width:100%;display:inline-flex;align-items:center;gap:6px;border:1.5px dashed transparent;
  background:none;border-radius:999px;padding:2px 4px;cursor:pointer;font-family:inherit;color:var(--navy);}
.vn2-sess:hover,.vn2-sess:focus-visible,.vn2-sess[aria-expanded="true"]{border-color:var(--mint-l);background:#f8fafc;}
.vn2-sc{font-size:.76rem;background:#f8fafc;border:1px solid var(--mint-ll);border-radius:999px;padding:2px 10px;white-space:nowrap;
  overflow:hidden;text-overflow:ellipsis;min-width:0;}
.vn2-sc b{font-weight:700;}
.vn2-sc-type{color:var(--slate);flex-shrink:2;}
.vn2-sess-gen .vn2-sc{background:#fffbeb;border-color:#fde68a;}
.vn2-car{color:var(--slate);font-size:.7rem;flex-shrink:0;}
/* F-25: the header chips always show in full. Instead of squeezing the chips to an ellipsis, the header and the
   title row wrap: the session chip keeps its natural width (its two chips go onto a 2nd line, and a chip's own text
   wraps, only when a whole row is too narrow); on >=768 the title takes the rest of the row or drops to its own line,
   and the actions move to the next row. Logical / direction-free, so RTL wraps the same way. */
.vn2-header{flex-wrap:wrap;row-gap:6px;}
.vn2-sess{flex-wrap:wrap;row-gap:4px;}
.vn2-sc{white-space:normal;overflow:visible;text-overflow:clip;overflow-wrap:break-word;max-width:100%;border-radius:12px;}
.vn2-sc-type{flex-shrink:1;}
@media (min-width:768px){
  .vn2-title-wrap{flex-wrap:wrap;row-gap:4px;min-width:min(100%,300px);}
  .vn2-sess{flex:0 1 auto;}
  .vn2-title{flex:1 1 140px;}
}
.vn2-sess-menu{position:fixed;z-index:470;background:#fff;border:1px solid var(--mint-l);border-radius:12px;padding:6px;overflow:auto;
  box-shadow:0 8px 24px rgba(13,148,136,.14),0 4px 8px rgba(0,0,0,.10);font-size:.8rem;}
.vn2-sm-h{font-size:.66rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:var(--slate);padding:8px 8px 4px;}
.vn2-sm-i{display:flex;justify-content:space-between;align-items:center;gap:8px;padding:7px 8px;border-radius:8px;cursor:pointer;}
.vn2-sm-i:hover,.vn2-sm-i:focus-visible{background:#f8fafc;outline:none;}
.vn2-sm-i-cur{background:var(--mint-ll);}
.vn2-sm-l{min-width:0;}
.vn2-sm-l b{font-weight:700;color:var(--navy);}
.vn2-sm-l small{display:block;color:var(--slate);font-size:.72rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.vn2-sm-st{flex-shrink:0;font-size:.68rem;font-weight:700;color:var(--slate);white-space:nowrap;}
.vn2-sm-cur{color:var(--teal-d);}
.vn2-sm-has{color:var(--navy);}
.vn2-sm-foot{border-top:1px solid var(--mint-ll);margin-top:4px;padding-top:4px;}
.vn2-sm-loading,.vn2-sm-err{padding:10px 8px;color:var(--slate);}
.vn2-move-body{margin-bottom:18px;color:#334155;font-size:14px;line-height:1.5;}
.vn2-move-btns{display:flex;flex-direction:column;gap:8px;}
.vn2-move-btns button[disabled]{opacity:.45;cursor:not-allowed;}
.vn2-lr-title{font-weight:800;font-size:.86rem;color:var(--navy);flex:1;min-width:0;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.vn2-lr-date{flex-shrink:0;font-size:.72rem;color:var(--slate);white-space:nowrap;}
.vn2-lr-read{flex-shrink:0;display:inline-flex;align-items:center;gap:5px;color:var(--teal);
  font-size:.66rem;font-weight:700;}
.vn2-lr-check{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;
  border-radius:50%;background:var(--teal);color:#fff;font-size:.66rem;font-weight:800;line-height:1;}
.vn2-lr-readlbl{display:none;}
.vn2-lr-preview{font-size:.76rem;color:var(--slate);line-height:1.4;padding-inline-start:1px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
/* Phone: date stacks under the title; the preview stays one line; read receipt shows its label */
@media (max-width:767px){
  .vn2-lr-main{flex-direction:column;align-items:flex-start;gap:1px;}
  .vn2-lr-title{flex:none;max-width:100%;}
  .vn2-lr-readlbl{display:inline;}
}

/* ══ N3 — empty state, restyled to the sheet's typography (same copy keys) ══ */
.vn2-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;text-align:center;
  padding:44px 20px;background:#f8fafc;border:1.5px dashed var(--mint-l);border-radius:var(--r-md);}
.vn2-empty-icon{font-size:1.6rem;}
.vn2-empty-title{font-weight:800;font-size:.95rem;color:var(--navy);}
.vn2-empty-body{font-size:.8rem;color:var(--slate);line-height:1.5;max-width:320px;}

/* ══ F-63 — the OVERLAY is the scroller (lab switch 22 "B10": lab branch test/f63-caret-lab @ 06dea6b; planning review 209) ══
   iPadOS standalone stops painting the text caret inside a SCROLLED inner overflow:auto scroller (the rich host). So nothing
   inside the sheet scrolls any more: the fixed overlay itself scrolls, and the sheet, root, body, panes and the editor grow
   with their content; the header and the formatting toolbar are sticky; the parent-summary pane and the past-sessions rail
   stay in view (sticky, one viewport tall); the floating past-session window lives in a viewport-tall sticky frame.
   Sticky offsets are measured from the overlay's CONTENT box, which sits --vn2-inset inside the screen edge, so the header uses top:-inset (it sticks flush with the screen)
   and --vn2-stk = --vn2-hdr - --vn2-inset puts the toolbar / side panes just under it. --vn2-inset = the overlay padding of the tier; --vn2-hdr = the header's height (measured in editor.js); --vn2-bsafe = the
   bottom safe area on phones. */
.vn2-overlay{--vn2-inset:24px;--vn2-bsafe:0px;--vn2-hdr:53px;--vn2-stk:calc(var(--vn2-hdr) - var(--vn2-inset));overflow-y:auto;overflow-x:hidden;display:block;-webkit-overflow-scrolling:touch;scroll-padding-bottom:80px;}
.vn2-overlay.vn2-tablet{--vn2-inset:16px;}
.vn2-overlay.vn2-phone{--vn2-inset:0px;--vn2-bsafe:env(safe-area-inset-bottom,0px);}
.vn2-sheet{display:block;flex:none;overflow:visible;height:auto;
  min-height:calc(100vh - 2*var(--vn2-inset));min-height:calc(100dvh - 2*var(--vn2-inset));}
.vn2-overlay.vn2-phone .vn2-sheet{padding-top:0;} /* the header carries the top safe area, so a sticky header also covers the notch */
.vn2-root{display:block;height:auto;overflow:visible;border-radius:inherit;
  min-height:calc(100vh - 2*var(--vn2-inset) - var(--vn2-bsafe));min-height:calc(100dvh - 2*var(--vn2-inset) - var(--vn2-bsafe));}
.vn2-header{position:sticky;top:calc(-1*var(--vn2-inset));z-index:6;background:#fff;border-top-left-radius:inherit;border-top-right-radius:inherit;}
.vn2-overlay.vn2-phone .vn2-header{padding-top:calc(8px + env(safe-area-inset-top,0px));}
.vn2-body{min-height:calc(100vh - 2*var(--vn2-inset) - var(--vn2-bsafe) - var(--vn2-hdr));min-height:calc(100dvh - 2*var(--vn2-inset) - var(--vn2-bsafe) - var(--vn2-hdr));}
.vn2-tb:not(.vn2-tb-pinned){position:sticky;top:var(--vn2-stk);z-index:5;}
.vn2-scroll{flex:1 0 auto;}
.vn2-rich-host{flex:1 0 auto;min-height:0;overflow:visible;}
.vn2-pane-clinical .vn2-ta-wrap{flex:1 0 auto;} /* the wrapper of the toolbar + the rich host: as tall as its content (the toolbar sticks inside it) */
@media (max-width:767px){
  .vn2-pane{flex:1 0 auto;}
  .vn2-pane-parent .vn2-collapse-inner,.vn2-pane-parent .vn2-scroll{flex:1 0 auto;}
  .vn2-pane-parent .vn2-ta-wrap,.vn2-pane-parent .vn2-ta{min-height:max(240px,calc(100dvh - var(--vn2-hdr) - var(--vn2-bsafe) - 230px));}
}
@media (min-width:768px){
  .vn2-pane-parent{position:sticky;top:var(--vn2-stk);align-self:flex-start;
    height:calc(100vh - 2*var(--vn2-inset) - var(--vn2-hdr));height:calc(100dvh - 2*var(--vn2-inset) - var(--vn2-hdr));}
  .vn2-pane-parent .vn2-scroll{flex:1;min-height:0;}
  .vn2-pane-parent.vn2-collapsed{position:static;align-self:stretch;height:auto;}
}
@media (min-width:1024px){
  .vn2-rail-past{position:sticky;top:var(--vn2-stk);align-self:flex-start;
    height:calc(100vh - 2*var(--vn2-inset) - var(--vn2-hdr));height:calc(100dvh - 2*var(--vn2-inset) - var(--vn2-hdr));}
}
/* the past-session window's frame: as tall as the visible sheet, glued to the bottom of the scrolled overlay, so the window's
   default / dragged / clamped position is always inside what the user sees (editor.js clamps against this frame) */
.vn2-peek-frame{position:sticky;bottom:0;z-index:30;pointer-events:none;
  height:calc(100vh - 2*var(--vn2-inset));height:calc(100dvh - 2*var(--vn2-inset));
  margin-top:calc(-1*(100vh - 2*var(--vn2-inset)));margin-top:calc(-1*(100dvh - 2*var(--vn2-inset)));}
.vn2-peek-frame .vn2-peek-win{pointer-events:auto;}
`;
  const st = document.createElement('style');
  st.id = 'vn2-styles';
  st.textContent = css;
  document.head.appendChild(st);
  _injected = true;
}
